/** Per-participant DSR action buttons — Phase 2B-dsr-3-ui.
 *
 * Mirrors `MemberDsrActions` (User-side) but for Participants:
 *   - Export → POST /v1/dsr/participants/{id}/export → JSON download.
 *   - Erase  → opens DsrParticipantEraseModal (typed confirmation). */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@/components/PermissionGate";

import {
  downloadParticipantExportBundle,
  exportParticipant,
} from "./api";
import { DsrParticipantEraseModal } from "./DsrParticipantEraseModal";
import type { SessionParticipant } from "./types";


type Props = { participant: SessionParticipant };


export function ParticipantDsrActions({ participant }: Props) {
  const { t } = useTranslation();
  const [eraseOpen, setEraseOpen] = useState(false);

  const exportM = useMutation({
    mutationFn: () => exportParticipant(participant.id),
    onSuccess: (bundle) => {
      downloadParticipantExportBundle({ bundle });
    },
  });

  return (
    <PermissionGate perm="dsr.execute">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => exportM.mutate()}
          disabled={exportM.isPending}
          title={t("ui.participantDsrActions.exportTitle")}
        >
          {exportM.isPending ? t("ui.participantDsrActions.exporting") : t("ui.participantDsrActions.export")}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
          onClick={() => setEraseOpen(true)}
          disabled={participant.erased}
          title={
            participant.erased
              ? t("ui.participantDsrActions.alreadyErased")
              : t("ui.participantDsrActions.eraseTitle")
          }
        >
          {participant.erased ? t("ui.participantDsrActions.erased") : t("ui.participantDsrActions.erase")}
        </button>
        {eraseOpen && (
          <DsrParticipantEraseModal
            participant={participant}
            onClose={() => setEraseOpen(false)}
          />
        )}
      </div>
    </PermissionGate>
  );
}
